<template lang="pug">
.NotificationsList.notifications
  .notifications-header(:class="{'is-notification': isSomeUnread}")
    h3(v-show="isSomeUnread") Unread
      span  Notifications
    transition(name="fade")
      NotificationsListMarkReadBtn.notifications-header__btn(
        v-show="isSomeUnread"
        :loading="notificationsCountsLoading",
        :value="!isSomeUnread",
        @click.native="onReadAll"
      )
  .notifications-content
    ul.notifications-content-items
        NotificationsListItem(
          v-for="item in groupUnreadItems",
          :key="item.id",
          v-bind="item",
          @read="onItemRead"
        )
  .notifications-content(
    v-for="(group) in groupedByDataItems",
  )
      .notifications-content__date {{getHumanizedDate(group[0].time)}}
      ul.notifications-content-items
        NotificationsListItem(
          v-for="item in group",
          :key="item.id",
          v-bind="item",
        )
  .notifications-btn-row(v-if="isShowLoadMoreLessButton")
    v-btn(
      color="primary",
      @click.prevent="loadMoreLess",
      role="button",
      :loading="loading",
      block
    ) {{`Load ${ loadedAll ? 'Less' : 'More' }`}}
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { scrollToTop } from 'Helpers/utils/view';
import { getHumanizedDate } from 'Helpers/utils/time';
import NotificationsListItem from './NotificationsListItem';
import NotificationsListMarkReadBtn from './NotificationsListMarkReadBtn';

export default {
  name: 'NotificationsList',
  components: {
    NotificationsListItem,
    NotificationsListMarkReadBtn,
  },
  props: {
    channel: {
      required: true,
      type: String,
      validate: value => ['system', 'updates'].includes(value),
    },
  },
  computed: {
    ...mapGetters('profile', [
      'isStatusDocument',
    ]),
    ...mapGetters('notifications', [
      'getNotificationsData',
      'getNotificationsLoading',
      'getNotificationsCount',
      'getNotificationsCountNew',
      'getIsNotificationsLoadedAll',
      'notificationsCountsLoading',

    ]),
    items() {
      return this.getNotificationsData(this.channel);
    },
    itemsCount() {
      return this.getNotificationsCount(this.channel);
    },
    itemsUnreadCount() {
      return this.getNotificationsCountNew(this.channel);
    },
    loading() {
      return this.getNotificationsLoading(this.channel);
    },
    loadedAll() {
      return this.getIsNotificationsLoadedAll(this.channel);
    },
    readItems() {
      if (!this.items.length) return this.items;
      return this.items.filter(item => item.read_flag === true);
    },
    unreadItems() {
      if (!this.items.length) return this.items;
      return this.items.filter(item => item.read_flag === false);
    },
    isSomeUnread() {
      return Boolean(this.unreadItems.length);
    },
    // grouped items by day
    groupedByDataItems() {
      const readItems = this.readItems.concat();
      if (!readItems.length) return readItems;
      // Sort by time (to be sure)
      readItems.sort((a, b) => new Date(b.time) - new Date(a.time));
      // Create first day and put first element in group
      const groups = [[readItems.shift()]];
      // iterate each item
      readItems.forEach((item) => {
        // get current (last) group
        const currentGroup = groups[groups.length - 1];
        // get current item as date
        const itemDate = new Date(item.time);
        // get current group date by first group item (all items in group should have same date)
        const currentGroupItemDate = new Date(currentGroup[0].time);
        // if date (day) the same
        if (itemDate.toDateString() === currentGroupItemDate.toDateString()) {
          // Put in current group
          currentGroup.push(item);
        } else {
          // Otherwise create new
          groups.push([item]);
        }
      });
      return groups;
    },
    groupUnreadItems() {
      return this.unreadItems;
    },
    isShowLoadMoreLessButton() {
      return this.itemsCount > 10;
    },
  },
  watch: {
    notifications: 'setRequiredDocumentNotificationAsRead',
    isStatusDocument: 'setRequiredDocumentNotificationAsRead',
  },
  methods: {
    ...mapActions('notifications', [
      'setNotificationAsRead',
      'moreNotifications',
      'lessNotifications',
      'setAllNotificationsAsRead',
      'replaceJustReadNotificationByUnread',
    ]),
    loadMoreLess() {
      if (!this.loadedAll) this.moreNotifications({ channel: this.channel });
      else {
        scrollToTop(100);
        setTimeout(() => {
          this.lessNotifications(this.channel);
        }, 200);
      }
    },
    setRequiredDocumentNotificationAsRead() {
      if (this.isStatusDocument) return;
      const docNotification = this.items.find(m => (
        m.message.text.startsWith('For your security, we require documents')
      ));
      if (docNotification && !docNotification.read_flag) {
        this.setNotificationAsRead(docNotification.id);
      }
    },
    onItemRead(itemId) {
      if (this.unreadItems.length < this.itemsUnreadCount) {
        this.replaceJustReadNotificationByUnread({ channel: this.channel, itemId });
      } else {
        this.setNotificationAsRead(itemId);
      }
    },
    onReadAll() {
      this.setAllNotificationsAsRead(this.channel);
    },
    getHumanizedDate,
  },
};
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.notifications
  &-header
    display: flex
    flex-direction: row
    justify-content: space-between
    align-items: center
    // margin-bottom: 10px
    h3
      margin-bottom: 10px
      span
        @media screen and (max-width: 767px)
          display: none
    .v-btn
      font-size: 14px !important
    &__btn
      opacity: 1
      transition-delay: 1.5s
      transition-property: opacity
      transition-timing-function: ease-out
      position: absolute
      right: 0
      top: 10px
  &-content
    display: flex
    flex-direction: column
    &__date
      font-family: $font-semibold
      font-weight: 600
      font-size: 17px
      line-height: 26px
      padding: 5px 0
      text-transform: capitalize
      color: $primary
    &-items
      list-style-type: none
      // overflow: hidden
    &:last-child
      .notification-item
        &:last-child
          &:before
            height: 50%
  &-btn-row
    display: flex
    flex-direction: row
    justify-content: center
    margin-top: 10px
</style>
